import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { PACK_KEYS } from '../lib';
import { PackApi } from '../api';
import type { PackBase, PackCreatePayload, PackUpdatePayload, PackItem, PackItemListParams } from './types';

export const usePackList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<PackBase>>({
    queryKey: PACK_KEYS.list(params),
    queryFn: () => PackApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const usePackById = (id: number) => {
  return useQuery<ApiResponse<PackBase>>({
    queryKey: PACK_KEYS.detail(id),
    queryFn: () => PackApi.getById(id),
    enabled: !!id,
  });
};

export const useCreatePack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PackCreatePayload) => PackApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACK_KEYS.all });
    },
  });
};

export const useUpdatePack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PackUpdatePayload) => PackApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACK_KEYS.all });
    },
  });
};

export const useDeletePack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => PackApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PACK_KEYS.all });
    },
  });
};

export const usePackItemList = (params?: PackItemListParams) => {
  return useQuery<ApiResponse<PackItem[]>>({
    queryKey: PACK_KEYS.itemList(params),
    queryFn: () => PackApi.getItemList(params),
    enabled: params !== undefined,
  });
};
